<template>
  <BaseDialog
    ref="baseDialog"
    modal-id="examScheduleFormDialog"
    :title="
      isEditMode
        ? $t('examScheduleForm.editTitle')
        : $t('examScheduleForm.addTitle')
    "
    :loading="loading"
    :confirm-text="$t('examScheduleForm.save')"
    @confirm="handleSubmit"
    @close="resetForm"
  >
    <template #body>
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-3 mb-3">
            <label class="form-label">{{ $t("examScheduleForm.day") }}</label>
          <div class="form-control-plaintext bg-light p-2 rounded">
  {{ form.day ? $t("examScheduleSettings.days." + form.day) : '-' }}
</div>
          </div>

          <div class="col-md-3 mb-3">
            <label class="form-label">{{
              $t("examScheduleForm.period")
            }}</label>
            <div class="form-control-plaintext bg-light p-2 rounded">
              {{ $t("examScheduleSettings.periodLabel", { num: form.period }) }}
            </div>
          </div>

          <div class="col-md-3 mb-3">
            <label class="form-label">{{
              $t("examScheduleForm.periodStart")
            }}</label>
            <div class="form-control-plaintext bg-light p-2 rounded">
              {{ formatTime(form.period_start) }}
            </div>
          </div>

          <div class="col-md-3 mb-3">
            <label class="form-label">{{
              $t("examScheduleForm.periodEnd")
            }}</label>
            <div class="form-control-plaintext bg-light p-2 rounded">
              {{ formatTime(form.period_end) }}
            </div>
          </div>

      <div class="col-md-6 mb-3">
    <label class="form-label">{{ $t('examScheduleForm.examType') }}</label>
    <div class="form-control-plaintext bg-secondary text-white p-2 rounded">
      {{ selectedExamType?.name || '' }}
    </div>
  </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">{{
              $t("examScheduleForm.examDate")
            }}</label>
            <div
              class="form-control-plaintext bg-secondary text-white p-2 rounded"
            >
              {{ formatDate(examDate) }}
            </div>
          </div>

          <div class="col-md-12 mb-3">
            <label class="form-label">{{
              $t("examScheduleForm.subject")
            }}</label>
            <select
              v-model="form.subject_id"
              class="form-select borderless-input"
              :class="{ 'is-invalid': errors.subject_id }"
              required
              :disabled="subjectsLoading"
            >
              <option value="">
                {{
                  subjectsLoading
                    ? $t("examScheduleForm.loadingSubjects")
                    : $t("examScheduleForm.selectSubject")
                }}
              </option>
              <option
                v-for="subject in filteredClassSubjects"
                :value="subject.id"
                :key="subject.id"
              >
                {{ subject.name }} ({{ subject.code }})
              </option>
            </select>
            <div v-if="errors.subject_id" class="invalid-feedback">
              {{ errors.subject_id }}
            </div>
            <small
              v-if="filteredClassSubjects.length === 0 && !subjectsLoading"
              class="text-muted"
            >
              {{ $t("examScheduleForm.noSubjects") }}
            </small>
          </div>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>
<script setup>
import {
  ref,
  computed,
  defineExpose,
  defineEmits,
  defineProps,
  onMounted,
} from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import BaseDialog from "@/components/common/BaseDialog.vue";

const { t, locale } = useI18n();

const props = defineProps({
  classId: {
    type: [String, Number],
    required: true,
  },
  className: {
    type: String,
    required: false,
  },
  examTypeId: [String, Number],
  examTypes: { type: Array, default: () => [] },
  examDate: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["refresh"]);

const store = useStore();
const toast = useToast();
const route = useRoute();
const baseDialog = ref(null);
const loading = ref(false);
const subjectsLoading = ref(false);
const isEditMode = ref(false);
const currentExamScheduleId = ref(null);
const selectedExamType = computed(() =>
  props.examTypes.find(t => t.id == props.examTypeId)
);
const periodMapping = {
  الأولى: "الاولى",
  الثانية: "الثانية",
  الثالثة: "الثالثة",
  الرابعة: "الرابعة",
  الخامسة: "الخامسة",
  السادسة: "السادسة",
  السابعة: "السابعة",
  الثامنة: "الثامنة",
};

const form = ref({
  day: "",
  period: "",
  period_start: "",
  period_end: "",
  subject_id: null,
  class_id: null,
  exam_type_id: null, 
    exam_date: "",
});

const errors = ref({});

const formatTime = (timeString) => {
  if (!timeString) return "";
  const [hours, minutes] = timeString.split(":");
  return `${hours}:${minutes}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value || "ar", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const allSubjects = computed(() => store.state.subjects?.subjects || []);

const filteredClassSubjects = computed(() => {
  const classId = parseInt(route.params.classId);
  return allSubjects.value.filter((subject) => subject.class_id === classId);
});

onMounted(async () => {
  await fetchSubjects();
});

const fetchSubjects = async () => {
  subjectsLoading.value = true;
  try {
    await store.dispatch("subjects/fetchSubjects");
  } catch (error) {
    toast.error(
      t("examScheduleForm.failedToLoadSubjects") ||
        "فشل في تحميل المواد الدراسية"
    );
    console.error("Error fetching subjects:", error);
  } finally {
    subjectsLoading.value = false;
  }
};

const openDialog = async (examSchedule = null) => {
  resetForm();

  if (allSubjects.value.length === 0) {
    await fetchSubjects();
  }

  form.value.class_id = parseInt(route.params.classId);
 form.value.exam_type_id = props.examTypeId;
  form.value.exam_date = props.examDate;

  if (examSchedule && examSchedule.id) {
    isEditMode.value = true;
    currentExamScheduleId.value = examSchedule.id;
    form.value = {
      ...form.value,
      day: examSchedule.day,
      period: examSchedule.period,
      period_start: examSchedule.period_start,
      period_end: examSchedule.period_end,
      subject_id: examSchedule.subject?.id || null,
    };
  } else {
    isEditMode.value = false;
    currentExamScheduleId.value = null;
    if (examSchedule) {
      form.value = {
        ...form.value,
        day: examSchedule.day || "",
        period: examSchedule.period || "",
        period_start: examSchedule.period_start || "",
        period_end: examSchedule.period_end || "",
        subject_id: null,
      };
    }
  }

  baseDialog.value.openDialog();
};

const validate = () => {
  let isValid = true;
  errors.value = {};

  if (!form.value.subject_id) {
    errors.value.subject_id =
      t("examScheduleForm.subjectRequired") || "يجب اختيار المادة";
    isValid = false;
  }

  if (!form.value.class_id) {
    errors.value.class_id =
      t("examScheduleForm.classIdRequired") || "معرف الفصل مطلوب";
    isValid = false;
  }

  if (!form.value.exam_type_id) {
    errors.value.exam_type_id =
      t("examScheduleForm.examTypeRequired") || "نوع الامتحان مطلوب";
    isValid = false;
  }

  if (!form.value.exam_date) {
    errors.value.exam_date =
      t("examScheduleForm.examDateRequired") || "تاريخ الامتحان مطلوب";
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  if (!validate()) return;

  try {
    loading.value = true;

    const backendPeriod = periodMapping[form.value.period] || form.value.period;

    const formData = {
      day: form.value.day,
      period: backendPeriod,
      period_start: form.value.period_start,
      period_end: form.value.period_end,
      subject_id: form.value.subject_id,
      class_id: form.value.class_id,
      exam_type_id: form.value.exam_type_id,
      exam_date: form.value.exam_date,
    };

    if (isEditMode.value && currentExamScheduleId.value) {
      await store.dispatch("examSchedules/updateExamSchedule", {
        classId: route.params.classId,
        id: currentExamScheduleId.value,
        data: formData,
      });
      toast.success(
        t("examScheduleForm.updateSuccess") || "تم تحديث جدول الامتحان بنجاح"
      );
    } else {
      await store.dispatch("examSchedules/createExamSchedule", {
        classId: route.params.classId,
        data: formData,
      });
      toast.success(
        t("examScheduleForm.addSuccess") || "تم إضافة الامتحان بنجاح"
      );
    }

    baseDialog.value.closeDialog();
    emit("refresh");
  } catch (error) {
    console.error("Error:", error);
    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors;
      toast.error(
        t("examScheduleForm.invalidData") || "يوجد أخطاء في البيانات المدخلة"
      );
    } else {
      toast.error(
        error.response?.data?.message ||
          t("examScheduleForm.saveFailed") ||
          "حدث خطأ أثناء حفظ البيانات"
      );
    }
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  form.value = {
    day: "",
    period: "",
    period_start: "",
    period_end: "",
    subject_id: null,
    class_id: null,
    exam_type_id: "",
    exam_date: "",
  };
  errors.value = {};
  isEditMode.value = false;
  currentExamScheduleId.value = null;
};

defineExpose({ openDialog });
</script>
